import { useQuery } from '@apollo/client/react';
import { Visibility, Edit } from '@mui/icons-material';
import { FormControlLabel, Switch, Button, Tooltip, Chip } from '@mui/material';
import { Grid } from '@mui/system';
import { EssealDataTable, type GridColDef, type GridRenderCellParams } from 'esseal-data-table';
import { capitalize } from 'lodash';
import { useState, useEffect } from 'react';

import TabPanel from '../TabPanel';
import { type SettingsModalTabProps } from '../types';

import { type CompanyResource, GetCompanyResourcesPaginatedDocument } from '@/graphql/generated/graphql';
import CompanyResourceModal from '@/shared-components/CompanyResourceModal';

const TYPE_LABELS: Record<string, string> = {
  human: 'Human',
  equipment: 'Equipment',
  material: 'Material',
  sub_contractor: 'Subcontractor',
};

const ResourcesManagementTab: React.FC<SettingsModalTabProps> = ({ parentTab, index, showHeading, fullHeight }) => {
  const [modalState, setModalState] = useState<{
    open: boolean;
    mode: 'create' | 'view' | 'edit';
    resource: CompanyResource | null;
  }>({
    open: false,
    mode: 'create',
    resource: null,
  });

  const [groupedData, setGroupedData] = useState(false);
  const { data, loading, fetchMore } = useQuery(GetCompanyResourcesPaginatedDocument, {
    variables: {
      first: 100,
    },
  });

  useEffect(() => {
    const pageInfo = data?.getCompanyResourcesPaginated?.pageInfo;

    if (!loading && pageInfo?.hasNextPage) {
      fetchMore({
        variables: { after: pageInfo.endCursor },
      });
    }
  }, [data, loading, fetchMore]);

  return (
    <TabPanel
      index={index}
      value={parentTab}
      showHeading={showHeading}
      heading="Resources Management"
      dataTestId="resource-management-tab"
      height={fullHeight ? '100%' : undefined}
    >
      <Grid container justifyContent="space-between" size={12}>
        <Grid
          container
          size={12}
          data-testid="company-resource-table"
          sx={{
            '& #custom-toolbar-buttons': {
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
            },
          }}
        >
          <EssealDataTable
            rows={(data?.getCompanyResourcesPaginated?.nodes as CompanyResource[]) || []}
            columns={columns}
            loading={loading}
            checkboxSelection={false}
            pagination={false}
            disableColumnMenu
            groupBy={groupedData ? ['resourceType'] : []}
            height="calc(100vh - 255px)"
            toolbar={
              <>
                <FormControlLabel
                  control={<Switch onClick={() => setGroupedData((prev) => !prev)} />}
                  label="Group by Type"
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setModalState({ open: true, mode: 'create', resource: null })}
                >
                  Create New
                </Button>
              </>
            }
            maxVisibleActions={3}
            rowActions={(_row) => [
              {
                label: 'View Details',
                icon: <Visibility sx={{ fontSize: 18 }} />,
                tooltipText: 'View Resource Details',
                onClick: (row) => setModalState({ open: true, mode: 'view', resource: row }),
              },
              {
                label: 'Edit Resource',
                icon: <Edit sx={{ fontSize: 18 }} />,
                tooltipText: 'Edit Resource Details',
                onClick: (row) => setModalState({ open: true, mode: 'edit', resource: row }),
              },
            ]}
          />
        </Grid>
      </Grid>

      {modalState.open && (
        <CompanyResourceModal
          open={modalState.open}
          handleClose={() => setModalState((prev) => ({ ...prev, open: false, resource: null }))}
          mode={modalState.mode}
          resource={modalState.resource}
        />
      )}
    </TabPanel>
  );
};

export default ResourcesManagementTab;

const columns: GridColDef<CompanyResource>[] = [
  {
    field: 'uniqueReference',
    headerName: 'ID / Code',
    width: 130,
    renderCell: ({ row }: GridRenderCellParams) => (
      <Tooltip title={row.uniqueReference || row.id}>
        <span>{row.uniqueReference || `CR-${row.id}`}</span>
      </Tooltip>
    ),
  },
  {
    field: 'name',
    headerName: 'Name',
    width: 260,
    renderCell: ({ row }: GridRenderCellParams) => (
      <Tooltip title={row.name}>
        <span style={{ fontWeight: 600 }}>{row.name}</span>
      </Tooltip>
    ),
  },
  {
    field: 'resourceType',
    headerName: 'Type',
    width: 140,
    renderCell: ({ row }: GridRenderCellParams) => (
      <Chip label={TYPE_LABELS[row.resourceType] || row.resourceType} size="small" variant="outlined" />
    ),
  },
  {
    field: 'source',
    headerName: 'Source',
    width: 110,
    renderCell: ({ row }: GridRenderCellParams) => <span>{capitalize(row.source || 'internal')}</span>,
  },
  {
    field: 'pointOfContact',
    headerName: 'Contact Name',
    width: 180,
    valueGetter: (row) => row.resourceMeta?.pointOfContact || '-',
  },
  {
    field: 'email',
    headerName: 'Email',
    width: 220,
    valueGetter: (row) => row.resourceMeta?.email || '-',
  },
  {
    field: 'primaryPhone',
    headerName: 'Phone',
    width: 160,
    valueGetter: (row) => row.resourceMeta?.primaryPhone || '-',
  },
];
